import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN, Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { PlatformUtil } from '@root/shared'
import { Textarea } from '@root/shared/ui-kit'

import { reatomImportWallets } from '../../atoms/reatom-port-wallets'

type Props = {
  onClose?: () => void
}
export const PortWalletsDrawerImport: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()
  const [text, setText] = useState<string>('')

  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomImportWallets(), [])
  const [state] = useAtom(model)
  const importWallets = useAction(model.execute)

  const fileInput = useRef<HTMLInputElement>(null)

  const handlePaste = async () => {
    const text = await PlatformUtil.readFromClipboard()

    if (Result.isSuccess(text) && text.success) {
      setText(text.success)
    }
  }

  // An export worth importing is thousands of lines — the file this was
  // written against is ninety-eight kilobytes across a hundred and seventy-nine
  // wallets. Selecting it beats asking anyone to copy that, and on a phone
  // there is no copying it at all.
  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Cleared so that picking the same file twice in a row still fires.
    event.target.value = ''
    if (!file) {
      return
    }
    setText(await file.text())
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(state)) {
      onClose?.()
      setText('')
    }
  }, [onClose, state])

  const handleImport = () => importWallets(text)
  const handleTextChange = (value: string) => setText(value)

  return (
    <Root>
      <Text
        value={text}
        onChange={handleTextChange}
        label={t('wallet-tracker.port.import-description')}
        rows={15}
      />
      <HiddenFileInput
        ref={fileInput}
        type="file"
        accept=".txt,.json,application/json,text/plain"
        onChange={handleFile}
      />
      <Footer>
        <FooterButton
          variant="soft"
          size="xs"
          radius="rounded"
          type="button"
          onClick={() => fileInput.current?.click()}
        >
          {t('wallet-tracker.port.import.choose-file')}
        </FooterButton>
        <FooterButton
          variant="soft"
          size="xs"
          radius="rounded"
          type="button"
          onClick={handlePaste}
        >
          {t('common.paste')}
        </FooterButton>
        <FooterButton
          variant="soft"
          size="xs"
          radius="rounded"
          type="button"
          disabled={!text}
          busy={state.loading}
          onClick={handleImport}
        >
          {t('wallet-tracker.port.tab.import')}
        </FooterButton>
      </Footer>
    </Root>
  )
}

const Root = styled.div`
  border-radius: 12px;
  padding: 18px 14px 0;
  overflow: auto;
  height: 100%;
  background: inherit;
`

const Text = styled(Textarea)``

const HiddenFileInput = styled.input`
  display: none;
`

const Footer = styled.footer`
  position: sticky;
  bottom: 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  padding: 16px 0 8px;
  background: inherit;
`

const FooterButton = styled(Button)`
  height: 34px;
  min-width: 100px;
  border-radius: 6px;
  padding-inline: 12px;
`
